import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import {
  LayoutDashboard,
  Scan,
  Upload,
  CreditCard,
  Settings,
  User,
  LogOut,
  ShoppingCart,
  Menu,
  X,
  MessageSquare,
  ChevronRight,
  Code,
  Gift,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LanguageSelector from "./LanguageSelector";

export default function Sidebar({ saasNumber = "", onHide = () => {} }) {
  const { t, i18n } = useTranslation();
  const { logout, user, updateUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth >= 1024; // Tailwind 'lg'
  });

  const handleLanguageChange = async (newLang) => {
    try {
      // Update i18n immediately for UI responsiveness
      i18n.changeLanguage(newLang);

      // Save to backend
      const response = await api.updateUser({
        language: newLang,
      });

      // Update local user state
      const updatedUser = response.user || response;
      updateUserData(updatedUser);
    } catch (error) {
      console.error("Error saving language preference:", error);
      // Fallback: at least keep the i18n change if API fails,
      // or we could revert it if we want to be strict.
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const handleChange = (e) => setIsDesktop(e.matches);

    setIsDesktop(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    // Safari/old browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Auto-close sidebar on navigation for mobile only
  useEffect(() => {
    if (!isDesktop) setIsMobileOpen(false);
  }, [location.pathname, isDesktop]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const menuItems = [
    { to: "/", icon: LayoutDashboard, label: t("sidebar.dashboard") },
    { to: "/scanners", icon: Scan, label: t("sidebar.scanners") },
    { to: "/upload", icon: Upload, label: t("sidebar.upload") },
    ...(user?.subscription_plan && user.subscription_plan !== "free"
      ? [{ to: "/tokens", icon: CreditCard, label: t("sidebar.tokens") }]
      : []),
    // La API y la documentación están abiertas a todos los planes (2-10-2026): los agentes y los desarrolladores
    // prueban antes de pagar; los límites los marca la cuota de páginas.
    { to: "/api", icon: Code, label: t("sidebar.api") },
    { to: "/referrals", icon: Gift, label: t("sidebar.referrals", "Invite & earn") },
    {
      to: "/pricing",
      icon: ShoppingCart,
      label: t("sidebar.pricing", "Pricing"),
    },
    { to: "/settings", icon: Settings, label: t("sidebar.settings") },
    {
      to: "/tickets",
      icon: MessageSquare,
      label: t("sidebar.tickets", "Tickets"),
    },
  ];

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        aria-label={t("sidebar.open", "Open menu")}
        data-track="menu_open"
        className="lg:hidden fixed top-4 left-4 z-50 bg-white/80 backdrop-blur-md p-2.5 rounded-xl shadow-lg border border-white/50 hover:shadow-xl transition-all duration-200 text-primary-600"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={() => setIsMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={false}
        animate={{
          x: isDesktop ? 0 : isMobileOpen ? 0 : "-100%",
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`w-72 fixed left-0 top-0 h-screen bg-white/80 backdrop-blur-2xl border-r border-white/60 shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-50 lg:translate-x-0 flex flex-col`}
      >
        {/* Mobile close button */}
        <button
          onClick={() => setIsMobileOpen(false)}
          aria-label={t("sidebar.close", "Close menu")}
          data-track="menu_close"
          className="lg:hidden absolute top-4 right-4 p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Logo Section */}
        <div className="p-8 pb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-auto h-auto bg-white from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <img src="/WhappScan.png" alt="Logo" className="w-auto h-auto" />
            </div>
            {/* <span className="font-bold text-xl text-gray-900 tracking-tight">WhappScan</span> */}
          </div>

          {/* <div className="bg-gradient-to-br from-primary-50 to-blue-50 border border-primary-100 p-4 rounded-xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
              <Scan className="w-16 h-16 text-primary-600 -rotate-12 transform translate-x-4 -translate-y-4" />
            </div>
            <div className="relative z-10">
              <p className="text-xs font-bold text-primary-400 uppercase tracking-wider mb-1">
                {t("sidebar.saas_number")}
              </p>
              <p className="text-sm font-bold text-primary-700 truncate">
                {saasNumber || t("sidebar.saas_number_empty")}
              </p>
            </div>
          </div> */}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 space-y-2 overflow-y-auto py-2 scrollbar-hide">
          {menuItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) =>
                `relative flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 group ${
                  isActive
                    ? "text-white shadow-lg shadow-primary-500/25"
                    : "text-gray-600 hover:bg-white hover:shadow-md hover:text-primary-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div
                      layoutId="sidebar-active"
                      className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl"
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                  <item.icon
                    className={`w-5 h-5 mr-3 relative z-10 transition-colors ${
                      isActive
                        ? "text-white"
                        : "text-gray-400 group-hover:text-primary-500"
                    }`}
                  />
                  <span className="font-medium relative z-10">
                    {item.label}
                  </span>
                  {isActive && (
                    <ChevronRight className="w-4 h-4 ml-auto text-white/80 relative z-10" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-100 bg-gray-50/50 space-y-3">
          <div className="flex items-center justify-center">
            <button
              onClick={() => {
                setIsMobileOpen(false);
              }}
              className="lg:hidden text-xs text-gray-500 hover:text-gray-700 bg-white/60 px-3 py-1 rounded-full border border-gray-100 shadow-sm transition-all"
            >
              {t("sidebar.hide", "Hide sidebar")}
            </button>
          </div>
          <div className="h-2" />
          <LanguageSelector
            value={user?.language || i18n.language}
            onChange={handleLanguageChange}
            className="mb-2"
          />

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => {
                navigate("/settings");
                setIsMobileOpen(false);
              }}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:border-primary-200 hover:text-primary-600 hover:shadow-md transition-all"
            >
              <User className="w-4 h-4" />
              <span className="truncate">{t("sidebar.profile")}</span>
            </button>
            <button
              onClick={() => {
                handleLogout();
                setIsMobileOpen(false);
              }}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-white border border-red-100 text-red-600 font-semibold text-sm hover:bg-red-50 hover:border-red-200 transition-all"
            >
              <LogOut className="w-4 h-4" />
              <span className="truncate">{t("sidebar.logout")}</span>
            </button>
          </div>
        </div>
      </motion.aside>
    </>
  );
}
